'use es6';

import I18n from 'I18n';
import { List, Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import * as Frequency from 'reporting-data/constants/frequency';
import * as Operators from 'reporting-data/constants/operators';
import { Exception } from 'reporting-data/exceptions';
import { getUseFiscalYear } from 'reporting-data/lib/fiscalYearUtils';
import makeDateRangeByType from 'reporting-data/lib/makeDateRangeByType';
import { getDataType, getDateRangeFilterValue, getReportConfig, getCustomFilters, getFilterGroups } from 'reporting-data/report/configReportGetters';
import { userInfo as getUserInfo } from 'reporting-data/request/user-info';
import { getQuotas } from 'reporting-ui-components/data/quotasDao';
import { getPipelineFilters } from 'reporting-ui-components/lib/goals/addGoalSeries';
import { getDailyGoals, getFrequencyEndDate, padBuckets, rebucket } from 'reporting-ui-components/lib/goals/calculations';
import { getReportOwners } from 'reporting-ui-components/reports/utils/quota';
const DATE_FORMAT = 'YYYY-MM-DD';
const getReportDates = (report, fiscalYearMonthName) => {
  const config = getReportConfig(report);
  const useFiscalYear = getUseFiscalYear(config);
  const dataType = getDataType(report);
  const dateRange = getDateRangeFilterValue(config);
  return makeDateRangeByType(dateRange.toJS(), DATE_FORMAT, dataType, useFiscalYear, fiscalYearMonthName);
};
const getRequestPayload = (report, {
  startDate,
  endDate
}, owners, pipelineFilter) => {
  const frequency = Frequency.MONTH;
  const nextEndDate = I18n.moment(getFrequencyEndDate({
    date: endDate,
    frequency
  })).format('YYYY-MM-01');
  const startMonth = I18n.moment(startDate).month() + 1;
  const startYear = I18n.moment(startDate).year();
  const endMonth = I18n.moment(nextEndDate).month() + 1;
  const endYear = I18n.moment(nextEndDate).year();
  const ownerIds = owners ? owners.map(owner => owner.get('ownerId')) : [];
  const assignees = ownerIds.length !== 0 ? {
    assignees: ownerIds,
    assigneesOperator: Operators.IN
  } : null;
  const config = getReportConfig(report);
  return Object.assign({
    customFilters: getCustomFilters(config),
    filterGroups: getFilterGroups(config)
  }, pipelineFilter, assignees, {
    dateRange: {
      startDate: {
        month: startMonth,
        year: startYear
      },
      endDate: {
        month: endMonth,
        year: endYear
      }
    }
  });
};
const getGoalTotal = (response, {
  startDate,
  endDate
}) => {
  const goalsByMonth = response.map(goal => ImmutableMap({
    date: goal.get('key'),
    value: goal.get('sum')
  }));
  const goalStart = response.first().get('key');
  const goalEnd = response.last().get('key');
  const paddedGoalsByMonth = padBuckets({
    goalsByMonth,
    startDate: goalStart,
    endDate: goalEnd
  });
  const dailyGoals = getDailyGoals({
    goalsByMonth: paddedGoalsByMonth
  });
  return rebucket({
    dailyGoals,
    dates: List([startDate, I18n.moment(endDate).add(1, 'days').format(DATE_FORMAT)])
  }).getIn([0, 'value'], null);
};
export const getQuotasTotal = ({
  report,
  fiscalYearMonthName
}) => {
  const goalType = 'REVENUE';
  const config = report.get('config');
  const pipelineFilter = getPipelineFilters(config);
  const {
    startDate,
    endDate
  } = getReportDates(report, fiscalYearMonthName);
  return getReportOwners(report).then(owners => {
    const requestPayload = getRequestPayload(report, {
      startDate,
      endDate
    }, owners, pipelineFilter);
    if (requestPayload.pipelinesOperator && ![Operators.HAS_PROPERTY, Operators.NOT_HAS_PROPERTY, Operators.IN, Operators.NOT_IN].includes(requestPayload.pipelinesOperator)) {
      // Prevent quota dao calls that will 400
      throw new Exception('Invalid Pipelines Operator');
    }
    return getQuotas(requestPayload, goalType);
  }).then(quotas => getGoalTotal(quotas, {
    startDate,
    endDate
  }))
  // Dont break the deal forecast widget if we can't get a valid quota config
  .catch(() => Promise.resolve(0));
};
const hasAnyScope = targetScopes => {
  return getUserInfo().then(({
    user: {
      scopes = []
    } = {}
  }) => {
    return scopes.findIndex(scope => targetScopes.includes(scope)) > -1;
  });
};
export function checkCanManageRevenueQuotas() {
  return hasAnyScope(ImmutableSet(['legacy-quotas-access', 'basic-sales-goal-manager', 'advanced-sales-goal-manager']));
}
export function checkCanReportOnRevenueQuotas() {
  return hasAnyScope(ImmutableSet(['sales-goal-assignment', 'advanced-sales-goal-assignment', 'basic-sales-goal-manager', 'advanced-sales-goal-manager', 'legacy-quotas-access', 'legacy-sales-goal-assignment']));
}